import React, { useState, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import PortraitPicker from './PortraitPicker';

// Static game data (mirrors server constants — same as RegisterPage)
const CLASSES = [
  { value: 'Fighter', label: 'Fighter',  desc: 'Sworn blade of the Accord. Masters of combat. Starts with Hide Breastplate, Hide Cap, Half Spear.',  hp: 10, mp: 0,  att: 5, def: 1 },
  { value: 'Mage',    label: 'Mage',     desc: 'Heir to the elemental seeds first planted at Brightspire. Wielders of arcane magic. Starts with Robes.',                  hp: 4,  mp: 24, att: 1, def: 4 },
  { value: 'Cleric',  label: 'Cleric',   desc: 'Channel of a chosen god. Divine healer empowered by your patron deity. Starts with Leather Breastplate.', hp: 6,  mp: 20, att: 2, def: 3 },
  { value: 'Thief',   label: 'Thief',    desc: 'Whisper of the Antarian alleys. Stealth and cunning. Starts with Leather Breastplate, Leather Cap, Dagger.',   hp: 8,  mp: 10, att: 3, def: 2 },
  { value: 'Psion',   label: 'Psion',    desc: 'Mind-reader from the Late-Thread schools. Master of psionic disciplines. Starts with Robes.',             hp: 4,  mp: 22, att: 2, def: 2 },
  { value: 'Druid',   label: 'Druid',    desc: 'Voice of the wild beyond the wall. Nature magic, charming, and summoning. Starts with Leather Breastplate.', hp: 8,  mp: 18, att: 2, def: 2 },
];

const RACES = [
  { value: 'Human',    label: 'Human',    bonus: '+3 VIT, +50 gold. Iconic card: Founding Accord (draw + heal + AP).' },
  { value: 'Dwarf',    label: 'Dwarf',    bonus: '+2 STR, +2 VIT, Health Potion. Iconic card: Stoneshield Rune (block wall opener).' },
  { value: 'High Elf', label: 'High Elf', bonus: '+2 MEN, +2 AGL, -1 VIT, Health & Mana Potion. Iconic card: Elven Precision (auto-hit + spell amp).' },
  { value: 'Drow',     label: 'Drow',     bonus: '+1 STR, +2 AGL, +1 MEN, -1 VIT, +50 gold. Iconic card: Shadowmeld (control + pierce).' },
  { value: 'Gnome',    label: 'Gnome',    bonus: '+5 MEN, -1 STR/AGL/VIT, Health & Mana Potion. Iconic card: First Planting (draw 2, scry, +1 AP).' },
  { value: 'Halfling', label: 'Halfling', bonus: '+2 AGL, +1 VIT, Shortsword. Iconic card: A Story Begun (lucky + permanent +ATT).' },
  { value: 'Half-Orc', label: 'Half-Orc', bonus: '+3 STR, +1 VIT, -1 AGL, Club. Iconic card: Civic Resolve (block + heal + ATT buff).' },
];

const ALIGNMENTS = [
  { value: 'LG', label: 'Lawful Good',    desc: 'Upholds law and protects the innocent' },
  { value: 'NG', label: 'Neutral Good',   desc: 'Does good without regard for rules' },
  { value: 'CG', label: 'Chaotic Good',   desc: 'Follows their conscience above all' },
  { value: 'LN', label: 'Lawful Neutral', desc: 'Values order and tradition' },
  { value: 'N',  label: 'True Neutral',   desc: 'Seeks balance in all things' },
  { value: 'CN', label: 'Chaotic Neutral', desc: 'Values personal freedom above all' },
  { value: 'LE', label: 'Lawful Evil',    desc: 'Uses the system for personal gain' },
  { value: 'NE', label: 'Neutral Evil',   desc: 'Self-interest without loyalty or rules' },
  { value: 'CE', label: 'Chaotic Evil',   desc: 'Destruction and chaos without remorse' },
];

const ELEMENTAL_SEEDS = [
  { value: 'Acid',        label: 'Acid',        spell: 'Acid Arrow' },
  { value: 'Air',         label: 'Air',         spell: 'Gust' },
  { value: 'Cold',        label: 'Cold',        spell: 'Chill Touch' },
  { value: 'Earth',       label: 'Earth',       spell: 'Tremor' },
  { value: 'Electricity', label: 'Electricity', spell: 'Shocking Grasp' },
  { value: 'Fire',        label: 'Fire',        spell: 'Burning Hands' },
  { value: 'Sonic',       label: 'Sonic',       spell: 'Lesser Sonic Boom' },
  { value: 'Water',       label: 'Water',       spell: 'Trickle' },
];

const DEITIES = [
  { value: 'Thormir', label: 'Thormir',  title: 'God of Healing and Light',      align: 'NG', spell: 'Cure Minor Wounds' },
  { value: 'Calimah', label: 'Calimah',  title: 'God of Evil and Darkness',      align: 'NE', spell: 'Blindness' },
  { value: 'Livella', label: 'Livella',  title: 'Goddess of Order and Healing',  align: 'LN', spell: 'Minor Shield of Law' },
  { value: 'Descrod', label: 'Descrod',  title: 'God of Chaos and Weakness',     align: 'CN', spell: 'Sap' },
  { value: 'Hantine', label: 'Hantine',  title: 'Goddess of Magic and Strength', align: 'N',  spell: 'Virtue' },
];

const DISCIPLINES = [
  { value: 'STR', label: 'Psychometabolism', desc: 'Perfecting the body through the mind',      stat: 'Strength',  spells: 'Inner Strength, Psionic Weakness' },
  { value: 'AGL', label: 'Psychomobility',   desc: 'Your mind increases your own motion',        stat: 'Agility',   spells: 'Agile Mind, Psionic Slowness' },
  { value: 'VIT', label: 'Psychokinesis',    desc: 'Your mind reaches out to move things',       stat: 'Vitality',  spells: 'Vital Destruction, Psychic Health' },
  { value: 'MEN', label: 'Clairsentience',   desc: 'Seeing the unseen, hearing the unheard',     stat: 'Mentality', spells: 'Mind Boost, Mental Crush' },
  { value: 'CHA', label: 'Telepathy',        desc: 'Mental communication and projection',        stat: 'Charisma',  spells: 'Encephalic Charisma, Aura of Awesomeness' },
];

const STAT_DESCRIPTIONS = {
  strength:  'Governs physical attacks and damage',
  agility:   'Governs speed and defense',
  vitality:  'Governs health and resistance',
  mentality: 'Governs mana and magic potency',
  charisma:  'Charm success & shop discounts',
};

// v641.9: Per-class loadout templates. Each template prescribes a 65-point
// stat allocation tuned to a common build for that class. Players can apply
// one with a single click on Step 2 and still tweak from there.
// Stats must each be 6..18 and sum to 65.
const CLASS_LOADOUTS = {
  Fighter: [
    { key: 'vanguard', label: '🛡️ Vanguard',   desc: 'Front-line bruiser. STR + VIT focus.', stats: { strength: 17, agility: 11, vitality: 16, mentality: 8,  charisma: 13 } },
    { key: 'duelist',  label: '⚔️ Duelist',    desc: 'Quick striker. STR + AGL focus.',      stats: { strength: 16, agility: 16, vitality: 13, mentality: 8,  charisma: 12 } },
  ],
  Mage: [
    { key: 'evoker',   label: '🔥 Evoker',     desc: 'Glass-cannon caster. MEN heavy.',       stats: { strength: 7,  agility: 12, vitality: 12, mentality: 18, charisma: 16 } },
    { key: 'battlemage', label: '⚡ Battlemage', desc: 'Sturdier caster. MEN + VIT.',          stats: { strength: 9,  agility: 11, vitality: 14, mentality: 18, charisma: 13 } },
  ],
  Cleric: [
    { key: 'healer',   label: '✨ Healer',      desc: 'Pure support. MEN + CHA.',              stats: { strength: 8,  agility: 11, vitality: 13, mentality: 17, charisma: 16 } },
    { key: 'crusader', label: '⚔️ Crusader',    desc: 'Hybrid front-line. STR + VIT + MEN.',   stats: { strength: 14, agility: 10, vitality: 14, mentality: 14, charisma: 13 } },
  ],
  Thief: [
    { key: 'shadow',   label: '🗡️ Shadow',     desc: 'Burst assassin. AGL + STR.',            stats: { strength: 14, agility: 18, vitality: 12, mentality: 8,  charisma: 13 } },
    { key: 'silvertongue', label: '💬 Silvertongue', desc: 'Charm-and-steal. AGL + CHA.',     stats: { strength: 10, agility: 16, vitality: 12, mentality: 9,  charisma: 18 } },
  ],
  Psion: [
    { key: 'kineticist', label: '🌀 Kineticist', desc: 'Mind-over-body striker. MEN + VIT.',  stats: { strength: 8,  agility: 12, vitality: 14, mentality: 18, charisma: 13 } },
    { key: 'seer',     label: '👁️ Seer',        desc: 'Pure mentalist. MEN + CHA.',           stats: { strength: 7,  agility: 11, vitality: 12, mentality: 18, charisma: 17 } },
  ],
  Druid: [
    { key: 'shapeshifter', label: '🐺 Shapeshifter', desc: 'Hybrid wild form. STR + VIT + MEN.', stats: { strength: 14, agility: 12, vitality: 14, mentality: 14, charisma: 11 } },
    { key: 'beastmaster',  label: '🦅 Beastmaster',  desc: 'Pet & charm focus. CHA + MEN.',     stats: { strength: 9,  agility: 12, vitality: 13, mentality: 15, charisma: 16 } },
  ],
};

const CreateCharacterPage = () => {
  const { isAuthenticated, createCharacter, selectCharacter } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Character fields
  const [characterName, setCharacterName] = useState('');
  const [charClass, setCharClass] = useState('');
  const [race, setRace] = useState('');
  const [alignment, setAlignment] = useState('');
  const [seed, setSeed] = useState('');
  const [deity, setDeity] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [portraitUrl, setPortraitUrl] = useState(null);
  const [stats, setStats] = useState({ strength: 13, agility: 13, vitality: 13, mentality: 13, charisma: 13 });

  const totalPoints = Object.values(stats).reduce((s, v) => s + v, 0);
  const remaining = 65 - totalPoints;

  const setStat = (key, val) => {
    const v = Math.max(6, Math.min(18, parseInt(val) || 6));
    setStats(prev => ({ ...prev, [key]: v }));
  };

  const raceInfo = useMemo(() => RACES.find(r => r.value === race), [race]);
  const classInfo = useMemo(() => CLASSES.find(c => c.value === charClass), [charClass]);

  // v641.8: "Surprise me" — picks a random valid combination of class, race,
  // alignment, dependent sub-choice (seed/deity/discipline), a generated
  // fantasy-ish name, and a random 65-point stat allocation respecting
  // the 6..18 per-stat bounds. Skips no fields so the result is submit-ready.
  const randomize = () => {
    const NAME_PARTS_A = ['Aer', 'Bran', 'Cor', 'Dae', 'El', 'Fae', 'Gar', 'Hal', 'Ilm', 'Jor', 'Kael', 'Lyr', 'Mor', 'Nyx', 'Oth', 'Pyr', 'Quel', 'Ral', 'Syl', 'Tal', 'Ul', 'Vor', 'Wyn', 'Xan', 'Ys', 'Zan'];
    const NAME_PARTS_B = ['avin', 'eth', 'ion', 'mar', 'ric', 'wyn', 'dor', 'ena', 'ius', 'lyn', 'oth', 'rin', 'sek', 'thar', 'vid', 'aris', 'eon', 'ath', 'or'];
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const newClass = pick(CLASSES).value;
    const newRace = pick(RACES).value;
    const newAlign = pick(ALIGNMENTS).value;
    const newName = pick(NAME_PARTS_A) + pick(NAME_PARTS_B);

    // 65-point stat allocation across 5 stats, each in [6..18].
    const keys = ['strength', 'agility', 'vitality', 'mentality', 'charisma'];
    const allocation = { strength: 6, agility: 6, vitality: 6, mentality: 6, charisma: 6 };
    let pool = 65 - 6 * 5; // = 35 points to distribute
    while (pool > 0) {
      const k = pick(keys);
      if (allocation[k] < 18) { allocation[k] += 1; pool -= 1; }
    }

    setCharacterName(newName);
    setCharClass(newClass);
    setRace(newRace);
    setAlignment(newAlign);
    setSeed(newClass === 'Mage' ? pick(ELEMENTAL_SEEDS).value : '');
    setDeity(newClass === 'Cleric' ? pick(DEITIES).value : '');
    setDiscipline(newClass === 'Psion' ? pick(DISCIPLINES).value : '');
    setStats(allocation);
    toast.success(`🎲 Surprise! Meet ${newName} the ${newRace} ${newClass}.`);
  };

  const needsSeed       = charClass === 'Mage';
  const needsDeity      = charClass === 'Cleric';
  const needsDiscipline = charClass === 'Psion';

  const canProceedStep1 = characterName.trim().length >= 2 && charClass && race && alignment
    && (!needsSeed || seed)
    && (!needsDeity || deity)
    && (!needsDiscipline || discipline);
  const canSubmit = canProceedStep1 && totalPoints === 65;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const onSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);

    const charData = {
      name: characterName,
      class: charClass,
      race,
      alignment,
      stats,
      ...(needsSeed && { seed }),
      ...(needsDeity && { deity }),
      ...(needsDiscipline && { discipline }),
      ...(portraitUrl && { portrait_url: portraitUrl }),
    };

    const result = await createCharacter(charData);
    if (result.success) {
      toast.success(`${characterName} has been created!`);
      // v685: auto-select the new character and play the opening cinematic
      // before dropping the player into the game world. If selection fails
      // for any reason, fall back to the character-select screen.
      const newId = result.character && result.character.id;
      if (newId) {
        const sel = await selectCharacter(newId);
        if (sel.success) {
          navigate('/opening-cinematic');
          setLoading(false);
          return;
        }
      }
      navigate('/characters');
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-form fade-in" style={{ maxWidth: 560 }}>
        <h2 style={{ textAlign: 'center', color: '#d4af37', marginBottom: 4 }}>Create New Character</h2>
        <p style={{ textAlign: 'center', color: '#8a8a9a', fontSize: 13, marginBottom: 16 }}>
          Step {step} of 2 {step === 1 ? '- Class, Race & Name' : '- Attributes'}
        </p>

        {/* v641.8: Surprise-me randomizer */}
        {step === 1 && (
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <button
              type="button"
              onClick={randomize}
              disabled={loading}
              style={{
                padding: '6px 14px',
                background: 'linear-gradient(180deg, #6a4a2a, #4a3018)',
                border: '1px solid #c9a84c',
                color: '#ffd96b',
                borderRadius: 18,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
              title="Roll a random class, race, alignment, name, and stats"
            >🎲 Surprise me</button>
          </div>
        )}

        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {[1, 2].map(s => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: s <= step ? '#d4af37' : '#2a2a3a',
              transition: 'background 0.3s'
            }} />
          ))}
        </div>

        {/* STEP 1: Name + Class + Race + Alignment */}
        {step === 1 && (
          <div>
            {/* Character Name */}
            <div className="form-group">
              <label>Character Name</label>
              <input type="text" value={characterName} onChange={e => setCharacterName(e.target.value)} disabled={loading} />
              {characterName && characterName.trim().length < 2 && <div className="error-message">Minimum 2 characters</div>}
            </div>

            {/* Class */}
            <div className="form-group">
              <label style={{ color: '#d4af37', fontSize: 14, fontWeight: 600 }}>Choose Your Class</label>
              <div className="rc-card-grid">
                {CLASSES.map(c => (
                  <div
                    key={c.value}
                    className={'rc-card' + (charClass === c.value ? ' rc-card-selected' : '')}
                    onClick={() => { setCharClass(c.value); setSeed(''); setDeity(''); setDiscipline(''); }}
                  >
                    <div className="rc-card-name">{c.label}</div>
                    <div className="rc-card-sub">{c.desc}</div>
                    <div className="rc-card-stats">HP {c.hp} | MP {c.mp} | ATT {c.att} | DEF {c.def}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mage: Elemental Seed */}
            {needsSeed && (
              <div className="form-group">
                <label style={{ color: '#7eb8da', fontSize: 14, fontWeight: 600 }}>Starting Elemental Seed</label>
                <p style={{ color: '#8a8a9a', fontSize: 12, margin: '2px 0 8px' }}>Seeds are elemental affinities that grow as you level. Your starting seed determines your first spell and unlocks a web of increasingly powerful elemental magic.</p>
                <div className="rc-card-grid rc-card-grid-sm">
                  {ELEMENTAL_SEEDS.map(s => (
                    <div key={s.value} className={'rc-card rc-card-sm' + (seed === s.value ? ' rc-card-selected' : '')} onClick={() => setSeed(s.value)}>
                      <div className="rc-card-name">{s.label}</div>
                      <div className="rc-card-sub">Spell: {s.spell}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cleric: Deity */}
            {needsDeity && (
              <div className="form-group">
                <label style={{ color: '#e8c170', fontSize: 14, fontWeight: 600 }}>Choose Your Patron Deity</label>
                <p style={{ color: '#8a8a9a', fontSize: 12, margin: '2px 0 8px' }}>Your deity determines your divine spells. Each god offers a different path of healing, protection, or destruction.</p>
                <div className="rc-card-grid rc-card-grid-sm">
                  {DEITIES.map(d => (
                    <div key={d.value} className={'rc-card rc-card-sm' + (deity === d.value ? ' rc-card-selected' : '')} onClick={() => setDeity(d.value)}>
                      <div className="rc-card-name">{d.label}</div>
                      <div className="rc-card-sub">{d.title} ({d.align})</div>
                      <div className="rc-card-sub">Prayer: {d.spell}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Psion: Discipline */}
            {needsDiscipline && (
              <div className="form-group">
                <label style={{ color: '#c8a0e8', fontSize: 14, fontWeight: 600 }}>Choose Your Primary Discipline</label>
                <p style={{ color: '#8a8a9a', fontSize: 12, margin: '2px 0 8px' }}>Your discipline shapes your psionic powers. Each branch focuses on a different mental stat and grants unique abilities.</p>
                <div className="rc-card-grid rc-card-grid-sm">
                  {DISCIPLINES.map(d => (
                    <div key={d.value} className={'rc-card rc-card-sm' + (discipline === d.value ? ' rc-card-selected' : '')} onClick={() => setDiscipline(d.value)}>
                      <div className="rc-card-name">{d.label}</div>
                      <div className="rc-card-sub">{d.desc}</div>
                      <div className="rc-card-sub">Focus: {d.stat} | {d.spells}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Race */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label style={{ color: '#d4af37', fontSize: 14, fontWeight: 600 }}>Choose Your Race</label>
              <div className="rc-card-grid rc-card-grid-sm">
                {RACES.map(r => (
                  <div key={r.value} className={'rc-card rc-card-sm' + (race === r.value ? ' rc-card-selected' : '')} onClick={() => setRace(r.value)}>
                    <div className="rc-card-name">{r.label}</div>
                    <div className="rc-card-sub">{r.bonus}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alignment */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label style={{ color: '#d4af37', fontSize: 14, fontWeight: 600 }}>Choose Your Alignment</label>
              <p style={{ color: '#8a8a9a', fontSize: 12, margin: '2px 0 8px' }}>Alignment affects which quests, factions, and NPC interactions are available to you. Hover over each for details.</p>
              <div className="rc-align-grid">
                {ALIGNMENTS.map(a => (
                  <div
                    key={a.value}
                    className={'rc-align-cell' + (alignment === a.value ? ' rc-align-selected' : '')}
                    onClick={() => setAlignment(a.value)}
                    title={a.desc}
                  >
                    {a.label}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={() => navigate('/characters')} style={{ flex: 1, background: '#333', color: '#ccc' }}>Back</button>
              <button className="btn btn-primary" disabled={!canProceedStep1} onClick={() => setStep(2)} style={{ flex: 2 }}>Next</button>
            </div>
          </div>
        )}

        {/* STEP 2: Stats */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ color: '#d4af37', marginBottom: 4, fontSize: 14 }}>
                Distribute 65 Attribute Points
              </h3>
              <p style={{ color: '#999', fontSize: 12, margin: 0 }}>
                Each stat: 6 to 18. Remaining: <strong style={{ color: remaining === 0 ? '#50c878' : remaining < 0 ? '#ff6b6b' : '#d4af37' }}>{remaining}</strong>
              </p>
              {raceInfo && (
                <p style={{ color: '#7eb8da', fontSize: 11, margin: '4px 0 0' }}>
                  {raceInfo.label} bonuses applied after allocation: {raceInfo.bonus}
                </p>
              )}
            </div>

            {/* v641.9: Class loadout presets */}
            {CLASS_LOADOUTS[charClass] && (
              <div style={{ marginBottom: 14, padding: '8px 10px', background: 'rgba(212, 175, 55, 0.06)', border: '1px solid rgba(212, 175, 55, 0.25)', borderRadius: 6 }}>
                <div style={{ color: '#d4af37', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Quick start: {charClass} loadouts
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {CLASS_LOADOUTS[charClass].map(lo => (
                    <button
                      key={lo.key}
                      type="button"
                      onClick={() => { setStats(lo.stats); toast.success(`Applied loadout: ${lo.label}`); }}
                      title={lo.desc}
                      style={{
                        padding: '5px 10px',
                        background: 'rgba(60, 80, 110, 0.45)',
                        border: '1px solid #4a6a90',
                        color: '#d8e6f5',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >{lo.label}</button>
                  ))}
                </div>
                <p style={{ color: '#8aa0bb', fontSize: 11, margin: '6px 0 0' }}>
                  Tap a preset to fill stats, then tweak as you like.
                </p>
              </div>
            )}

            <div className="rc-stat-grid">
              {Object.entries(stats).map(([key, val]) => (
                <div key={key} className="rc-stat-row">
                  <div className="rc-stat-label">
                    <span className="rc-stat-name">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                    <span className="rc-stat-desc">{STAT_DESCRIPTIONS[key]}</span>
                  </div>
                  <div className="rc-stat-controls">
                    <button className="rc-stat-btn" onClick={() => setStat(key, val - 1)} disabled={val <= 6}>-</button>
                    <input
                      type="number"
                      className="rc-stat-input"
                      value={val}
                      onChange={e => setStat(key, e.target.value)}
                      min={6} max={18}
                    />
                    <button className="rc-stat-btn" onClick={() => setStat(key, val + 1)} disabled={val >= 18}>+</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="rc-summary">
              <div className="rc-summary-title">Character Summary</div>
              <div className="rc-summary-row"><span>Name:</span><strong>{characterName}</strong></div>
              <div className="rc-summary-row"><span>Class:</span><strong>{charClass}</strong></div>
              <div className="rc-summary-row"><span>Race:</span><strong>{race}</strong></div>
              <div className="rc-summary-row"><span>Alignment:</span><strong>{ALIGNMENTS.find(a => a.value === alignment)?.label}</strong></div>
              {needsSeed && <div className="rc-summary-row"><span>Element:</span><strong>{seed}</strong></div>}
              {needsDeity && <div className="rc-summary-row"><span>Deity:</span><strong>{deity}</strong></div>}
              {needsDiscipline && <div className="rc-summary-row"><span>Discipline:</span><strong>{DISCIPLINES.find(d => d.value === discipline)?.label}</strong></div>}
              {classInfo && (
                <div className="rc-summary-row"><span>Base:</span><strong>HP {classInfo.hp} | MP {classInfo.mp} | ATT {classInfo.att} | DEF {classInfo.def}</strong></div>
              )}
            </div>

            {/* v679: Portrait selector — optional, falls back to default if skipped */}
            <div className="form-group" style={{ marginTop: 14 }}>
              <label style={{ color: '#d4af37', fontSize: 14, fontWeight: 600 }}>Choose Your Portrait <span style={{ color: '#8a8a9a', fontSize: 11, fontWeight: 400 }}>(optional — can be changed later)</span></label>
              <PortraitPicker
                race={race}
                selected={portraitUrl}
                onSelect={setPortraitUrl}
                showClear
                compact
              />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={() => setStep(1)} style={{ flex: 1, background: '#333', color: '#ccc' }}>Back</button>
              <button
                className="btn btn-primary"
                disabled={loading || !canSubmit}
                onClick={onSubmit}
                style={{ flex: 2 }}
              >
                {loading ? 'Creating...' : 'Create Character'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateCharacterPage;
